import { ContactModelV2, IPhoneNumber } from 'Models/ContactModelV2';
import {
  MIN_PHONE_DIGITS,
  MIXED_CONTACTS_MESSAGE,
  TOOLTIP_MESSAGES,
} from 'Modules/conversation/createConversation/components/createConversationForm/constants';
import {
  ContactOptionType,
  ContactTypeFilterResolverProps,
  ContactTypeFilterResolverReturn
} from 'Modules/conversation/createConversation/types';
import {
  getContactType,
  getContactTypeLabel,
  analyzeSelectedContacts,
} from 'Modules/conversation/createConversation/utils';
import { useMemo, useCallback } from 'react';
import { bugsnagClient } from 'Utils/logUtils';
import { OptionType } from '~/lib/ui/autocomplete/index.types';
import {
  createPhoneNumberOption,
  filterOption,
} from '~/lib/ui/autocomplete/utils';
import { ContactTypeValue } from '../index.types';
import {
  cleanPhoneNumber,
  isPhoneNumber,
  isPhoneNumberAlreadySelected,
} from '../utils';

/**
 * Maps ContactModelV2 to ContactOptionType for autocomplete
 * @param contact - The contact model to convert
 * @returns ContactOptionType object for the autocomplete component
 */
export const mapContactToOption = (
  contact: ContactModelV2
): ContactOptionType => {
  const type = getContactType(contact.source);

  const join = (...parts: (string | undefined)[]) =>
    parts.filter(Boolean).join(', ');

  const description =
    type === 'internal-contact'
      ? join(contact.department, contact.role)
      : type === 'external-contact'
        ? join('External Contact', contact.organization, contact.role)
        : getContactTypeLabel(contact.source);

  return {
    value: contact.source === 'Bhive' ? contact.sourceId : contact.phoneNumber,
    label: contact.name,
    title: contact.name,
    description,
    avatar: contact.avatarUrl,
    type,
    id: contact.id,
    sourceId: contact.sourceId,
    ...contact,
  };
};

/**
 * Checks if a contact is already selected by comparing phone numbers or sourceIds
 * @param contact - The contact to check
 * @param selectedContacts - Array of already selected contacts
 * @returns true if the contact is already selected, false otherwise
 */
const isContactAlreadySelected = (
  contact: ContactOptionType,
  selectedContacts: ContactOptionType[]
): boolean => {
  return selectedContacts.some((selected) => {
    // For external contacts, compare phone numbers
    if (contact.type === 'external-contact' && contact.value) {
      const contactPhone = cleanPhoneNumber(contact.value);
      const selectedPhone = cleanPhoneNumber(selected.value || '');
      if (contactPhone && selectedPhone && contactPhone === selectedPhone) {
        return true;
      }
    }

    // For internal contacts, compare sourceId
    if (
      contact.type === 'internal-contact' &&
      contact.value &&
      selected.value
    ) {
      return contact.value === selected.value;
    }

    return false;
  });
};

/**
 * Marks contacts as disabled if they are already selected
 * @param contacts - Array of contacts to process
 * @param selectedContacts - Array of already selected contacts
 * @returns Array of contacts with disabled status updated
 */
const markAlreadySelectedContacts = (
  contacts: ContactOptionType[],
  selectedContacts: ContactOptionType[]
): ContactOptionType[] => {
  return contacts.map((contact) => {
    const isAlreadySelected = isContactAlreadySelected(
      contact,
      selectedContacts
    );

    if (isAlreadySelected) {
      return {
        ...contact,
        isDisabled: true,
        tooltipMessage: TOOLTIP_MESSAGES['already-selected'],
      };
    }

    return contact;
  });
};

/**
 * Determines if a contact should be disabled based on conversation type restrictions
 */
const shouldDisableContactByType = (
  contact: ContactOptionType,
  contactAnalysis: ReturnType<typeof analyzeSelectedContacts>
): boolean => {
  const { firstContactType, hasInternal, hasAnyExternal, isMixed } =
    contactAnalysis;

  if (isMixed) {
    return contact.type !== firstContactType;
  }

  if (hasInternal && !hasAnyExternal) {
    return contact.type !== 'internal-contact';
  }

  if (hasAnyExternal && !hasInternal) {
    return contact.type !== 'external-contact';
  }

  return false;
};

/**
 * Processes a single contact with both "already selected" and type-based logic
 */
const processContactWithRestrictions = (
  contact: ContactOptionType,
  contactAnalysis: ReturnType<typeof analyzeSelectedContacts>,
  selectedContacts: ContactOptionType[],
  searchQuery: string,
  baseContacts: ContactOptionType[]
): ContactOptionType => {
  // Check if already selected first (highest priority) - ALWAYS apply this logic
  if (isContactAlreadySelected(contact, [...selectedContacts])) {
    return {
      ...contact,
      isDisabled: true,
      tooltipMessage: TOOLTIP_MESSAGES['already-selected'],
    };
  }

  // Then check type-based restrictions
  if (shouldDisableContactByType(contact, contactAnalysis)) {
    return { ...contact, isDisabled: true };
  }

  return contact;
};

/**
 * Filters and processes contact options based on conversation type rules
 */
const processContactOptions = (
  baseContacts: ContactOptionType[],
  contactAnalysis: ReturnType<typeof analyzeSelectedContacts>,
  typedPhoneOption: ContactOptionType | null,
  selectedContacts: ContactOptionType[],
  searchQuery = ''
): ContactOptionType[] => {
  const { firstContactType } = contactAnalysis;

  let processedContacts = [...baseContacts];
  const shouldAddTypedOption = !!typedPhoneOption;

  if (!firstContactType) {
    processedContacts = markAlreadySelectedContacts(processedContacts, [
      ...selectedContacts,
    ]);

    if (shouldAddTypedOption) {
      return [typedPhoneOption, ...processedContacts];
    }
    return processedContacts;
  }

  // For conversation type restrictions, apply both "already selected" and type-based logic
  processedContacts = processedContacts.map((contact) =>
    processContactWithRestrictions(
      contact,
      contactAnalysis,
      selectedContacts,
      searchQuery,
      baseContacts
    )
  );

  if (shouldAddTypedOption) {
    return [typedPhoneOption, ...processedContacts];
  }
  return processedContacts;
};

/**
 * Creates a phone number option if the search query is a valid phone number
 * Always creates the typed phone number option to match TopBar behavior
 */
const createTypedPhoneNumberOption = (
  searchQuery: string,
  selectedContacts: ContactOptionType[],
  baseContacts: ContactOptionType[]
): ContactOptionType | null => {
  if (!searchQuery || !isPhoneNumber(searchQuery)) {
    return null;
  }

  const cleanedQuery = cleanPhoneNumber(searchQuery);

  if (cleanedQuery.length < MIN_PHONE_DIGITS) {
    return null;
  }

  // Check if this number is already selected
  const isAlreadySelected = isPhoneNumberAlreadySelected(
    searchQuery,
    selectedContacts
  );

  const contactAnalysis = analyzeSelectedContacts(selectedContacts);
  let isDisabled = false;
  let tooltipMessage: string | undefined;

  // Determine if the phone number option should be disabled based on current selection
  if (isAlreadySelected) {
    isDisabled = true;
    tooltipMessage = TOOLTIP_MESSAGES['already-selected'];
  } else if (contactAnalysis.hasInternal && contactAnalysis.count > 0) {
    // If internal contacts are selected, disable phone numbers
    isDisabled = true;
    tooltipMessage = TOOLTIP_MESSAGES['mixed-internal-first'];
  }

  // Use the raw searchQuery instead of formatted version to show exact number typed
  const phoneOption = createPhoneNumberOption(
    searchQuery,
    isDisabled,
    tooltipMessage
  ) as ContactOptionType;

  return phoneOption;
};

/**
 * Gets the appropriate tooltip message based on conversation type
 */
const getTooltipMessage = (
  contactAnalysis: ReturnType<typeof analyzeSelectedContacts>
): string | null => {
  const { firstContactType, isMixed } = contactAnalysis;

  if (!firstContactType) return null;

  if (isMixed) {
    bugsnagClient.notify(new Error(MIXED_CONTACTS_MESSAGE), (event) => {
      event.severity = 'info';
      event.context = 'getTooltipMessage';
      event.addMetadata('custom', {
        function: 'getTooltipMessage',
        message: MIXED_CONTACTS_MESSAGE,
      });
    });
    return null;
  }

  return TOOLTIP_MESSAGES[firstContactType] || null;
};

/**
 * Enhanced hook to resolve contact type filtering and tooltip logic
 * Now includes phone number detection and real-time option creation
 */
export function useContactTypeResolver({
  selectedContacts,
  baseContacts,
  searchQuery = '',
}: ContactTypeFilterResolverProps): ContactTypeFilterResolverReturn {
  // Memoize base contacts to prevent unnecessary recalculations
  const baseContactOptions = useMemo(() => baseContacts, [baseContacts]);

  // Analyze selected contacts once
  const contactAnalysis = useMemo(
    () => analyzeSelectedContacts([...selectedContacts]),
    [selectedContacts]
  );

  // Create typed phone number option if applicable
  const typedPhoneOption = useMemo(() => {
    const result = createTypedPhoneNumberOption(
      searchQuery,
      [...selectedContacts],
      baseContactOptions
    );

    return result;
  }, [searchQuery, selectedContacts, baseContactOptions]);

  // Process contact options based on contact analysis and phone option
  const processedContactOptions = useMemo(() => {
    const result = processContactOptions(
      baseContactOptions,
      contactAnalysis,
      typedPhoneOption,
      [...selectedContacts],
      searchQuery
    );

    return result;
  }, [
    baseContactOptions,
    contactAnalysis,
    typedPhoneOption,
    selectedContacts,
    searchQuery,
  ]);

  // Get tooltip message
  const tooltipMessage = useMemo(
    () => getTooltipMessage(contactAnalysis),
    [contactAnalysis]
  );

  // Memoized tooltip info getter
  const getTooltipInfo = useCallback(
    (option: ContactOptionType) => {
      // Check for custom tooltip message first (like "already selected")
      if (option.tooltipMessage) {
        return { show: true, message: option.tooltipMessage };
      }

      // Default tooltip logic
      return option.isDisabled && tooltipMessage
        ? { show: true, message: tooltipMessage }
        : null;
    },
    [tooltipMessage]
  );

  // Determine if first contact is internal
  const isFirstContactInternal = useMemo(() => {
    return contactAnalysis.isFirstContactInternal;
  }, [contactAnalysis]);

  return {
    processedContactOptions,
    getTooltipInfo,
    isFirstContactInternal,
  };
}

/**
 * Custom filter function for conversation module autocomplete component
 *
 * This filter is specifically designed for the conversation module's autocomplete component
 * and should be used by consumers within the same domain to maintain the component's
 * reusability while providing conversation-specific filtering logic.
 *
 * The filter implements a multi-tier approach:
 * 1. **Generic filtering**: Uses the standard autocomplete filter for basic text matching
 * 2. **Phone number filtering**: Provides enhanced phone number search capabilities
 * 3. **Contact phone matching**: Matches saved contacts by their phone numbers
 */
export const createContactFilterOption = (
  option: { data: OptionType<ContactTypeValue> },
  inputValue: string
) => {
  if (!inputValue) {
    return true;
  }

  // Use the generic filter first
  const genericResult = filterOption(option, inputValue);
  if (genericResult) {
    return true;
  }

  // Enhanced phone number search for contacts
  const isPhoneSearch = isPhoneNumber(inputValue);
  if (isPhoneSearch) {
    const cleanedInput = cleanPhoneNumber(inputValue);

    // Check the main contact value (for both external and internal contacts)
    if (option.data.type === 'external-contact' && option.data.value) {
      const cleanedContactPhone = cleanPhoneNumber(option.data.value);
      // Only check if both cleaned values are non-empty
      if (cleanedContactPhone && cleanedInput) {
        // Check for exact match or partial matches in both directions
        if (
          cleanedContactPhone === cleanedInput ||
          cleanedContactPhone.includes(cleanedInput) ||
          cleanedInput.includes(cleanedContactPhone)
        ) {
          return true;
        }
      }
    }

    // Check additional phone numbers if available
    const phoneNumbers = option.data.phoneNumbers || [];
    const phoneNumberMatch = phoneNumbers.some((phone: IPhoneNumber) => {
      const cleanedPhone = cleanPhoneNumber(phone.number || '');
      // Only check if both cleaned values are non-empty
      if (cleanedPhone && cleanedInput) {
        return (
          cleanedPhone === cleanedInput ||
          cleanedPhone.includes(cleanedInput) ||
          cleanedInput.includes(cleanedPhone)
        );
      }
      return false;
    });

    if (phoneNumberMatch) {
      return true;
    }
  }

  return false;
};
